import type {
  AnnouncementStep,
  GameOutcome,
  GamePhase,
  NightRoleStep,
  OneNightAction,
  PlayerState,
  RoleId,
  VoteResult,
} from "@cloudnest/one-night-ultimate-werewolf-engine";
import { useSyncExternalStore } from "react";
import { isPlatformGamePath } from "../../shared/routing/game-path-mode";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
} from "../../shared/services/game-api";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function resolveOneNightGameApiMode(): GameApiMode {
  if (typeof window === "undefined") return "legacy";
  return isPlatformGamePath(
    "one-night-ultimate-werewolf",
    window.location.pathname,
  )
    ? "game-platform"
    : "legacy";
}

function getGameApi(): GameApi {
  const nextMode = resolveOneNightGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("one-night-ultimate-werewolf", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

type CardVisibility = "hidden" | "known" | "revealed";
type CenterSlots = readonly [string | null, string | null, string | null];

export interface ProjectedCard {
  readonly cardId: string;
  readonly roleId: RoleId | null;
  readonly visibility: CardVisibility;
}

export interface PublicNightRoleStep {
  readonly roleId: RoleId;
  readonly status: NightRoleStep["status"];
}

export type RevealedCardLocation =
  | {
      readonly kind: "player";
      readonly playerId: string;
      readonly cardId: string;
    }
  | {
      readonly kind: "center";
      readonly slotIndex: 0 | 1 | 2;
      readonly cardId: string;
    };

export interface ViewerNightAction {
  readonly roleId: RoleId;
  readonly status: "required" | "waiting" | "completed";
  readonly canInspectCenter?: boolean;
  /** Actor-only: the role the doppelganger copied. */
  readonly copiedRoleId?: RoleId;
}

export interface OneNightPublicState {
  readonly gameId: string;
  readonly createdAt: string;
  readonly version: number;
  readonly phase: GamePhase;
  readonly hostPlayerId: string;
  readonly players: readonly PlayerState[];
  readonly seatOrder: readonly string[];
  readonly seatLocked: boolean;
  readonly selectedRoleIds: readonly RoleId[];
  readonly rolePeekConfirmedPlayerIds: readonly string[];
  readonly discussionDurationSeconds: number;
  readonly discussionRemainingSeconds: number | null;
  readonly voteWindowSeconds: number;
  readonly voteRemainingSeconds: number | null;
  readonly autoVotePlayerIds: readonly string[];
  readonly voteLockedPlayerIds: readonly string[];
  readonly centerSlots: CenterSlots;
  readonly nightRoleQueue: readonly PublicNightRoleStep[];
  readonly announcerTimeline: readonly AnnouncementStep[];
  readonly voteResult: VoteResult | null;
  readonly revealedCardIds: readonly string[];
  readonly revealedCardLocations: readonly RevealedCardLocation[];
  readonly gameOutcome: GameOutcome | null;
}

export interface OneNightViewerState extends OneNightPublicState {
  readonly viewerPlayerId: string | null;
  readonly viewerCurrentCardId: string | null;
  readonly visibleCards: Readonly<Record<string, ProjectedCard>>;
  readonly viewerNightAction: ViewerNightAction | null;
}

export interface GameStoreState {
  readonly roomId: string | null;
  readonly gameState: OneNightPublicState | null;
  readonly viewerState: OneNightViewerState | null;
  readonly myPlayerId: string | null;
  readonly isHost: boolean;
  readonly lastAppliedSeq: number;
  readonly error: string | null;
}

interface OneNightActionPayload {
  readonly schemaVersion: 1;
  readonly action: OneNightAction;
}

interface ViewerStateRow {
  readonly sequenceNumber: number;
  readonly state: Record<string, unknown>;
}

interface BootstrapDescriptor {
  readonly __bootstrap: true;
  readonly gameId: string;
  readonly hostPlayerId: string;
  readonly createdAt: string;
  readonly playerSeating: readonly {
    readonly playerId: string;
    readonly displayName: string;
  }[];
}

const initialState: GameStoreState = {
  roomId: null,
  gameState: null,
  viewerState: null,
  myPlayerId: null,
  isHost: false,
  lastAppliedSeq: 0,
  error: null,
};

let state: GameStoreState = { ...initialState };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

function makeActionPayload(action: OneNightAction): OneNightActionPayload {
  return { schemaVersion: 1, action };
}

function makeBootstrapDescriptor(
  roomId: string,
  hostPlayerId: string,
  playerIds: readonly string[],
  playerNames: readonly string[] = [],
): BootstrapDescriptor {
  return {
    __bootstrap: true,
    gameId: roomId,
    hostPlayerId,
    createdAt: new Date().toISOString(),
    playerSeating: playerIds.map((playerId, index) => ({
      playerId,
      displayName: playerNames[index]?.trim() || `플레이어 ${index + 1}`,
    })),
  };
}

function publicStateFromViewerState(
  viewerState: OneNightViewerState,
): OneNightPublicState {
  return {
    gameId: viewerState.gameId,
    createdAt: viewerState.createdAt,
    version: viewerState.version,
    phase: viewerState.phase,
    hostPlayerId: viewerState.hostPlayerId,
    players: viewerState.players,
    seatOrder: viewerState.seatOrder,
    seatLocked: viewerState.seatLocked,
    selectedRoleIds: viewerState.selectedRoleIds,
    rolePeekConfirmedPlayerIds: viewerState.rolePeekConfirmedPlayerIds,
    discussionDurationSeconds: viewerState.discussionDurationSeconds,
    discussionRemainingSeconds: viewerState.discussionRemainingSeconds,
    voteWindowSeconds: viewerState.voteWindowSeconds,
    voteRemainingSeconds: viewerState.voteRemainingSeconds,
    autoVotePlayerIds: viewerState.autoVotePlayerIds,
    voteLockedPlayerIds: viewerState.voteLockedPlayerIds,
    centerSlots: viewerState.centerSlots,
    nightRoleQueue: viewerState.nightRoleQueue,
    announcerTimeline: viewerState.announcerTimeline,
    voteResult: viewerState.voteResult,
    revealedCardIds: viewerState.revealedCardIds,
    revealedCardLocations: viewerState.revealedCardLocations,
    gameOutcome: viewerState.gameOutcome,
  };
}

function adoptViewerState(
  viewerState: OneNightViewerState,
  sequenceNumber: number,
): void {
  state = {
    ...state,
    viewerState,
    gameState: publicStateFromViewerState(viewerState),
    lastAppliedSeq: sequenceNumber,
    error: null,
  };
}

function adoptLatest(
  rows: readonly ViewerStateRow[],
  fromSeq: number,
): boolean {
  let bestSeq = fromSeq;
  let bestState: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      bestState = row.state;
    }
  }
  if (bestState === null) {
    return false;
  }
  adoptViewerState(bestState as unknown as OneNightViewerState, bestSeq);
  return true;
}

export const gameActions = {
  async initializeAsHost(
    roomId: string,
    myPlayerId: string,
    participantUserIds: readonly string[],
    participantNames: readonly string[] = [],
  ): Promise<void> {
    state = {
      ...initialState,
      roomId,
      myPlayerId,
      isHost: true,
    };
    notify();

    const existing = await getGameApi().getViewerStates(roomId);
    if (existing.success && existing.data && existing.data.length > 0) {
      adoptLatest(existing.data as readonly ViewerStateRow[], 0);
      notify();
      return;
    }

    const descriptor = makeBootstrapDescriptor(
      roomId,
      myPlayerId,
      participantUserIds,
      participantNames,
    );
    const initResponse = await getGameApi().submitEvent(roomId, "init", {
      state: descriptor as unknown as Record<string, unknown>,
    });
    if (!initResponse.success) {
      // 초기화 경합(effect 중복 발화 등)으로 다른 init이 먼저 상태를 만든
      // 경우다 — 저장된 투영을 다시 읽어 복구한다. 그대로 끝내면 위에서
      // 리셋한 상태 때문에 호스트가 '플레이어를 기다리는 중'에 갇힌다.
      await gameActions.replayEvents(roomId);
      return;
    }

    const bootstrapResponse = await getGameApi().submitEvent(
      roomId,
      "BootstrapMaterialize",
      {
        actionId: `${roomId}:bootstrap`,
      },
    );
    if (!bootstrapResponse.success) {
      // init이 이미 상태를 구체화했으면 BootstrapMaterialize는 거절된다 —
      // WS 푸시가 구조해주는 레이스에 기대지 않고 저장된 투영을 직접 읽는다.
      await gameActions.replayEvents(roomId);
      return;
    }

    await gameActions.replayEvents(roomId);
  },

  async initializeAsGuest(roomId: string, myPlayerId: string): Promise<void> {
    state = {
      ...initialState,
      roomId,
      myPlayerId,
      isHost: false,
    };
    notify();
    await gameActions.replayEvents(roomId);
  },

  async replayEvents(roomId: string): Promise<void> {
    const response = await getGameApi().getViewerStates(
      roomId,
      state.lastAppliedSeq,
    );
    if (!response.success || !response.data) {
      state = {
        ...state,
        error: response.error ?? "상태를 불러오지 못했습니다",
      };
      notify();
      return;
    }

    adoptLatest(
      response.data as readonly ViewerStateRow[],
      state.lastAppliedSeq,
    );
    notify();
  },

  applyRemoteViewerState(
    payloadState: Record<string, unknown>,
    sequenceNumber: number,
  ): void {
    if (sequenceNumber <= state.lastAppliedSeq) return;
    adoptViewerState(
      payloadState as unknown as OneNightViewerState,
      sequenceNumber,
    );
    notify();
  },

  applyRemoteEvent(
    _eventType: string,
    _payload: Record<string, unknown>,
    _sequenceNumber: number,
  ): void {
    // Hidden-information rendering uses per-recipient `game:viewer_state`.
    // Raw game events are intentionally ignored so they cannot drive UI state.
  },

  async submitAction(action: OneNightAction): Promise<boolean> {
    if (!state.roomId) return false;

    const roomId = state.roomId;
    const response = await getGameApi().submitEvent(
      roomId,
      "action",
      makeActionPayload(action) as unknown as Record<string, unknown>,
    );
    if (!response.success) {
      state = {
        ...state,
        error: response.error ?? "행동 전송에 실패했습니다",
      };
      notify();
      return false;
    }

    await gameActions.replayEvents(roomId);
    return true;
  },

  reset(): void {
    state = { ...initialState };
    notify();
  },
};

export const gameStore = {
  getState: (): GameStoreState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useGameStore(): GameStoreState {
  return useSyncExternalStore(gameStore.subscribe, gameStore.getState);
}

export function useGameActions(): typeof gameActions {
  return gameActions;
}
